/**
 * IANA timezone helpers for the admin Live Zoom Class scheduler.
 *
 * The rest of the app has no date library (date-fns ships as an unused
 * shadcn peer dependency), so these use the native `Intl` API rather than
 * add one — `Intl.DateTimeFormat` with a `timeZone` option is enough to do
 * correct zoned <-> UTC conversion without pulling in date-fns-tz/luxon for
 * what is, in the end, one admin form.
 */

/** Every IANA zone name Chromium/Firefox/Safari all know about. Falls back to
 * a curated list on older browsers that predate `Intl.supportedValuesOf`
 * (Safari < 15.4) — small enough to keep here rather than a separate file. */
const FALLBACK_TIMEZONES = [
  "UTC", "Africa/Lagos", "Africa/Cairo", "Africa/Johannesburg", "Africa/Nairobi",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Toronto", "America/Sao_Paulo", "America/Mexico_City",
  "Asia/Karachi", "Asia/Kolkata", "Asia/Colombo", "Asia/Dhaka", "Asia/Dubai",
  "Asia/Riyadh", "Asia/Qatar", "Asia/Baghdad", "Asia/Tehran", "Asia/Kabul",
  "Asia/Singapore", "Asia/Kuala_Lumpur", "Asia/Jakarta", "Asia/Manila",
  "Asia/Hong_Kong", "Asia/Shanghai", "Asia/Tokyo", "Asia/Seoul", "Asia/Istanbul",
  "Australia/Sydney", "Australia/Perth",
  "Europe/London", "Europe/Dublin", "Europe/Paris", "Europe/Berlin",
  "Europe/Madrid", "Europe/Rome", "Europe/Moscow", "Europe/Oslo",
  "Europe/Stockholm", "Europe/Istanbul",
  "Pacific/Auckland",
];

// A handful of zones Chromium's own `supportedValuesOf('timeZone')` still
// reports under a legacy/colonial-era spelling, even though the IANA tzdb
// renamed them years ago — confirmed empirically: its list has
// "Asia/Calcutta" and "Africa/Asmera", never "Asia/Kolkata" or
// "Africa/Asmara". Both names in each pair are the exact same zone/rules
// (aliases, not different offsets), so remapping is purely cosmetic —
// showing the name people actually search for — never a behavioural change.
// India is this app's largest single user base (see Admin > Analytics), so
// "Asia/Kolkata" not appearing at all was the one most worth catching.
const LEGACY_TO_MODERN_NAME = {
  "Asia/Calcutta": "Asia/Kolkata",
  "Asia/Rangoon": "Asia/Yangon",
  "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Africa/Asmera": "Africa/Asmara",
  "Asia/Katmandu": "Asia/Kathmandu",
  "Asia/Dacca": "Asia/Dhaka",
  "Asia/Thimbu": "Asia/Thimphu",
  "Asia/Ujung_Pandang": "Asia/Makassar",
  "America/Godthab": "America/Nuuk",
};

export function listTimezones() {
  let zones = null;
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      const all = Intl.supportedValuesOf("timeZone");
      if (all && all.length) zones = all;
    }
  } catch {
    /* fall through to the curated list */
  }
  if (!zones) zones = FALLBACK_TIMEZONES;
  zones = zones.map((z) => LEGACY_TO_MODERN_NAME[z] || z);
  // "UTC" is a fully valid Intl/zoneinfo timezone — `Intl.DateTimeFormat({
  // timeZone: "UTC" })` resolves it cleanly — but at least Chromium's own
  // `supportedValuesOf('timeZone')` omits it from the enumeration entirely
  // (confirmed directly: its 418-entry list has no "UTC" and no "Etc/UTC").
  // Guarantee it's always selectable rather than leaving it to ICU's choice
  // of what counts as "supported" vs. merely "valid".
  return zones.includes("UTC") ? zones : ["UTC", ...zones];
}

/** "Asia/Kolkata" -> "Asia/Kolkata (GMT+5:30)". The offset is computed as of
 * *now* purely to label the option list — the actual conversion always uses
 * the real offset at the chosen date, via `zonedTimeToUtcIso` below, so a
 * DST changeover between "now" and the scheduled date never affects
 * correctness, only this cosmetic label. */
export function timezoneLabel(tz) {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "shortOffset" })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName");
    return part ? `${tz.replace(/_/g, " ")} (${part.value})` : tz.replace(/_/g, " ");
  } catch {
    return tz.replace(/_/g, " ");
  }
}

/** The zone's UTC offset, in ms, at a given instant (positive = east of UTC). */
function offsetMsAt(instantMs, timeZone) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone, hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const p = Object.fromEntries(dtf.formatToParts(new Date(instantMs)).map((x) => [x.type, x.value]));
  // Re-reading that wall-clock as if it were UTC gives back an instant that's
  // exactly `timeZone`'s current offset away from the one we started with.
  const asUtc = Date.UTC(p.year, Number(p.month) - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - instantMs;
}

/** "2026-08-15" + "19:30" + "Asia/Kolkata" -> the UTC instant at which clocks
 * in Kolkata read 19:30 on the 15th (ISO string). Two passes so a scheduled
 * time that falls right on a DST changeover still converges correctly —
 * a single pass is the more common shortcut but can be off by the DST
 * delta in that narrow window. */
export function zonedTimeToUtcIso(dateStr, timeStr, timeZone) {
  if (!dateStr || !timeStr) return null;
  const [y, mo, d] = dateStr.split("-").map(Number);
  const [h, mi] = timeStr.split(":").map(Number);
  const naiveMs = Date.UTC(y, mo - 1, d, h, mi, 0);
  if (Number.isNaN(naiveMs)) return null;
  let guess = naiveMs;
  for (let i = 0; i < 2; i++) guess = naiveMs - offsetMsAt(guess, timeZone);
  return new Date(guess).toISOString();
}

/** The inverse: a stored UTC instant -> the {date, time} pair that reads as
 * on a wall clock in `timeZone` — how the admin's own Date/Time inputs
 * repopulate when editing an existing schedule. */
export function utcIsoToZonedParts(iso, timeZone) {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  // en-CA conveniently formats the date part as YYYY-MM-DD.
  const dtf = new Intl.DateTimeFormat("en-CA", {
    timeZone, hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
  const p = Object.fromEntries(dtf.formatToParts(d).map((x) => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
}

/** The browser's own zone — used as a friendly default for a schedule that
 * has never been set before (an existing saved zoom_timezone always wins
 * over this), and as the "which zone is this viewer actually in" input to
 * `timezoneAbbreviation()` below. Normalized through the same legacy-alias
 * map as `listTimezones()` — confirmed empirically that a browser whose OS
 * is set to "Asia/Kolkata" can have `resolvedOptions().timeZone` itself
 * report back "Asia/Calcutta", the same ICU quirk, not just in the
 * enumeration list. */
export function browserTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    return LEGACY_TO_MODERN_NAME[tz] || tz;
  } catch {
    return "UTC";
  }
}

// `Intl`'s own `timeZoneName: "short"` only returns a real letter
// abbreviation for a narrow set of zones — confirmed empirically it's
// mostly just North America (EST/EDT, PST/PDT, ...); most of the rest,
// including Europe/London and Asia/Kolkata, fall back to a bare "GMT+1" /
// "GMT+5:30" instead of "BST" / "IST". The two tables below cover the gap:
// a fixed table for zones that never observe DST (a single abbreviation is
// correct year-round), and a [standard, daylight] pair for zones that do,
// resolved by comparing the target date's own offset against that zone's
// year-round minimum (see `timezoneAbbreviation`).
const FIXED_ZONE_ABBREVIATIONS = {
  "Asia/Kolkata": "IST",
  "Asia/Colombo": "IST",
  "Asia/Karachi": "PKT",
  "Asia/Dhaka": "BDT",
  "Asia/Kathmandu": "NPT",
  "Asia/Kabul": "AFT",
  "Asia/Tehran": "IRST",
  "Asia/Dubai": "GST",
  "Asia/Muscat": "GST",
  "Asia/Qatar": "AST",
  "Asia/Riyadh": "AST",
  "Asia/Baghdad": "AST",
  "Asia/Kuwait": "AST",
  "Asia/Bahrain": "AST",
  "Asia/Singapore": "SGT",
  "Asia/Kuala_Lumpur": "MYT",
  "Asia/Jakarta": "WIB",
  "Asia/Manila": "PHT",
  "Asia/Hong_Kong": "HKT",
  "Asia/Shanghai": "CST",
  "Asia/Tokyo": "JST",
  "Asia/Seoul": "KST",
  "Africa/Lagos": "WAT",
  "Africa/Nairobi": "EAT",
  "Africa/Cairo": "EET",
  "Africa/Johannesburg": "SAST",
};

/** [standard, daylight] — deliberately excludes zones with unusual DST
 * conventions (e.g. Europe/Dublin, whose *standard* time is legally "IST"
 * and treats winter as the negative-offset exception) rather than risk
 * getting those backwards. */
const DST_ZONE_ABBREVIATIONS = {
  "Europe/London": ["GMT", "BST"],
  "Europe/Paris": ["CET", "CEST"],
  "Europe/Berlin": ["CET", "CEST"],
  "Europe/Madrid": ["CET", "CEST"],
  "Europe/Rome": ["CET", "CEST"],
  "Europe/Oslo": ["CET", "CEST"],
  "Europe/Stockholm": ["CET", "CEST"],
  "Australia/Sydney": ["AEST", "AEDT"],
};

/** The zone's "standard" (non-DST) offset for the given year, found without
 * hardcoding transition dates or assuming a hemisphere: DST always pushes
 * the clock forward relative to standard time, so whichever of Jan 1 /
 * Jul 1 has the smaller offset is standard — correct whether the zone's
 * summer is in July (northern hemisphere, e.g. London) or January
 * (southern, e.g. Sydney). */
function standardOffsetMsForYear(year, timeZone) {
  const jan = offsetMsAt(Date.UTC(year, 0, 1), timeZone);
  const jul = offsetMsAt(Date.UTC(year, 6, 1), timeZone);
  return Math.min(jan, jul);
}

/** "7:00 PM" -> "IST" for a specific instant in `timeZone` — the label
 * appended to the student-facing schedule time, e.g. "7:00 PM IST". Trusts
 * Intl's own answer first (so the zones it already gets right, mostly North
 * American ones, are never overridden), then the DST-aware pair table, then
 * the fixed table, in that order. */
export function timezoneAbbreviation(date, timeZone) {
  try {
    const fromIntl = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
      .formatToParts(date)
      .find((p) => p.type === "timeZoneName")?.value || "";
    if (fromIntl && !/^GMT/.test(fromIntl)) return fromIntl;

    const pair = DST_ZONE_ABBREVIATIONS[timeZone];
    if (pair) {
      const isStandard = offsetMsAt(date.getTime(), timeZone) === standardOffsetMsForYear(date.getUTCFullYear(), timeZone);
      return isStandard ? pair[0] : pair[1];
    }

    return FIXED_ZONE_ABBREVIATIONS[timeZone] || fromIntl;
  } catch {
    return "";
  }
}
